import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Camera, Save, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { Link } from 'react-router-dom';

const ProfileUpdate = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
      setPhotoPreview(user.photo_url);
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = { ...formData };
      if (photo) {
        updateData.photo = photo;
      }

      const response = await usersAPI.update(user.id, updateData);
      
      // Update the user context with new data
      updateUser(response.data);
      
      setSuccess('Profile updated successfully!');
      
      // Clear photo state after successful update
      setPhoto(null);
      
      // Optional: hide success message after a few seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.errors || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden text-gray-900">
          <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
            {/* Profile Photo Section */}
            <div className="flex flex-col sm:flex-row items-center gap-8 pb-8 border-b border-gray-100">
              <div className="relative group">
                <div className="h-32 w-32 rounded-full overflow-hidden bg-green-50 flex items-center justify-center border-4 border-white shadow-lg ring-1 ring-gray-100">
                  {photoPreview ? (
                    <img
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      src={photoPreview}
                      alt="Profile"
                    />
                  ) : (
                    <div className="flex flex-col items-center">
                      <User className="w-12 h-12 text-green-200" />
                      <span className="text-green-700 text-2xl font-bold mt-1">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-xl cursor-pointer transition-all hover:scale-110 active:scale-95 group border-2 border-white">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-bold text-gray-900">Profile Picture</h3>
                <p className="text-sm text-gray-500 mt-1 mb-4 leading-relaxed max-w-xs">
                  Upload a high-quality photo to help others recognize you.
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Max Size: 5MB</span>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 gap-6">
              {/* Name Field */}
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-bold text-gray-700 ml-1 flex items-center gap-2">
                  <div className="p-1 bg-green-50 rounded-lg">
                    <User className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  className="block w-full px-5 py-4 rounded-2xl border-gray-200 bg-gray-50/50 border focus:bg-white focus:ring-4 focus:ring-green-500/10 focus:border-green-500 transition-all outline-none"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-bold text-gray-700 ml-1 flex items-center gap-2">
                  <div className="p-1 bg-green-50 rounded-lg">
                    <Mail className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  className="block w-full px-5 py-4 rounded-2xl border-gray-200 bg-gray-50/50 border focus:bg-white focus:ring-4 focus:ring-green-500/10 focus:border-green-500 transition-all outline-none"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone Field */}
              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-bold text-gray-700 ml-1 flex items-center gap-2">
                  <div className="p-1 bg-green-50 rounded-lg">
                    <Phone className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  required
                  className="block w-full px-5 py-4 rounded-2xl border-gray-200 bg-gray-50/50 border focus:bg-white focus:ring-4 focus:ring-green-500/10 focus:border-green-500 transition-all outline-none"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>

              {/* Address Field */}
              <div className="space-y-2">
                <label htmlFor="address" className="text-sm font-bold text-gray-700 ml-1 flex items-center gap-2">
                  <div className="p-1 bg-green-50 rounded-lg">
                    <MapPin className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  Detailed Delivery Address
                </label>
                <textarea
                  name="address"
                  id="address"
                  rows={4}
                  required
                  className="block w-full px-5 py-4 rounded-2xl border-gray-200 bg-gray-50/50 border focus:bg-white focus:ring-4 focus:ring-green-500/10 focus:border-green-500 transition-all outline-none resize-none"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your complete apartment, street, and city details"
                />
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center gap-3 p-5 bg-red-50 border border-red-100 rounded-2xl animate-shake">
                <div className="p-1.5 bg-red-100 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-sm font-bold text-red-800">
                  {Array.isArray(error) ? error.join(', ') : error}
                </p>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-3 p-5 bg-green-50 border border-green-100 rounded-2xl animate-fade-in">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-sm font-bold text-green-800">{success}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all shadow-lg hover:shadow-green-200/50 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Changes
                  </>
                )}
              </button>
              <Link
                to="/dashboard"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-2xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98]"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileUpdate;
